import React, { useState, useRef, useEffect } from 'react';
import { db } from '../db';
import { Upload, Download, ArrowLeft, Trash2, Settings as SettingsIcon, MessageSquare, ShieldAlert, Bug, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast, confirmDialog } from '../utils/feedback';
import { FeedbackModal } from '../components/FeedbackButton';

const Settings = ({ currentProfile, onUpdate, onLogout }) => {
  const navigate = useNavigate();
  const [name, setName] = useState(currentProfile?.name || '');
  const [pin, setPin] = useState(currentProfile?.pin || '');
  const [avatarUrl, setAvatarUrl] = useState(currentProfile?.avatarUrl || '');
  const [bannerGradient, setBannerGradient] = useState(currentProfile?.bannerGradient || 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))');
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const fileInputRef = useRef(null);
  
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  const resizeImage = (file, maxWidth, maxHeight) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      const base64 = await resizeImage(file, 600, 600);
      setAvatarUrl(base64);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pin.length !== 4) return;
    setSaving(true);
    
    const updatedProfile = {
      ...currentProfile,
      name,
      pin,
      avatarUrl: avatarUrl,
      bannerGradient
    };
    await db.updateProfile(updatedProfile);
    setSaving(false);
    onUpdate(updatedProfile);
    toast.success("Paramètres mis à jour !");
  };

  const handleExportJSON = async () => {
    try {
      const data = await db.exportProfileData(currentProfile.id);
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(data, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `masse-terclass-export-${currentProfile.name}.json`);
      document.body.appendChild(downloadAnchorNode); 
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      toast.success("Exportation réussie !");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'exportation");
    }
  };

  const handleDeleteProfile = () => {
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    try {
      setSaving(true);
      await db.deleteProfile(currentProfile.id);
      localStorage.removeItem('fitTrackProfileId');
      toast.success("Profil supprimé avec succès.");
      onUpdate(null);
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression du profil.");
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!currentProfile) return null;

  return (
    <div style={{ padding: '1rem', maxWidth: '1000px', margin: '0 auto', animation: 'fadeIn 0.3s ease-out' }}>
      <button className="btn" onClick={() => navigate(-1)} style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
        <ArrowLeft size={20} /> Retour
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '2rem', alignItems: 'start' }}>
        <div className="card" style={{ marginBottom: 0, padding: 0, overflow: 'hidden' }}>
          <div style={{ height: '100px', background: bannerGradient || 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' }}></div>
          <div style={{ padding: '0 1.5rem 1.5rem', marginTop: '-60px', position: 'relative', zIndex: 10 }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 0 }}>
                  <div 
                    style={{ 
                      width: '120px', height: '120px', borderRadius: '50%', backgroundColor: 'var(--bg-color)', 
                      display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', 
                      overflow: 'hidden', border: '4px solid var(--bg-card)', position: 'relative',
                      boxShadow: 'var(--shadow)',
                      transition: 'border-color 0.2s'
                    }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {avatarUrl ? (
                      <img loading="lazy" src={avatarUrl} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <Upload size={40} color="var(--text-secondary)" />
                    )}
                  </div>
                  <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '0.5rem', marginBottom: 0 }}>Cliquez pour modifier</p>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    style={{ display: 'none' }} 
                    accept="image/*" 
                    onChange={handleFileChange} 
                  />
                </div>
                
              </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '0.3rem' }}>Pseudo</label>
            <input 
              type="text" 
              className="form-control" 
              value={name} 
              onChange={(e) => setName(e.target.value)} 
              required 
            />
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '0.3rem' }}>Code PIN (4 chiffres)</label>
            <input 
              type="password" 
              inputMode="numeric"
              pattern="[0-9]*"
              className="form-control" 
              value={pin} 
              onChange={(e) => setPin(e.target.value.slice(0, 4))} 
              required 
              minLength="4" 
              maxLength="4" 
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label" style={{ marginBottom: '0.3rem' }}>Couleur de la bannière</label>
            <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
              {[
                { name: 'Classique', value: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' },
                { name: 'Nuit', value: 'linear-gradient(135deg, #141e30, #243b55)' },
                { name: 'Aurore', value: 'linear-gradient(135deg, #0B486B, #F56217, #ff6a00)' },
                { name: 'Vaporwave', value: 'linear-gradient(135deg, #ff00cc, #333399)' },
                { name: 'Magma', value: 'linear-gradient(135deg, #000000, #8A0404, #FF3B00)' },
                { name: 'Toxique', value: 'linear-gradient(135deg, #131A22, #00D287, #BFFF00)' },
                { name: 'Galaxie', value: 'radial-gradient(circle at top right, #9D50BB, #6E48AA, #000000)' },
                { name: 'Hologramme', value: 'linear-gradient(45deg, #ff9a9e, #fecfef, #8ec5fc, #e0c3fc)' }
              ].map((gradient, index) => (
                <div 
                  key={index}
                  title={gradient.name}
                  onClick={() => setBannerGradient(gradient.value)}
                  style={{ 
                    width: '40px', 
                    height: '40px',
                    minWidth: '40px',
                    flexShrink: 0,
                    borderRadius: '50%', 
                    background: gradient.value,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: bannerGradient === gradient.value ? '0 0 0 3px var(--text-primary), 0 0 10px rgba(0,0,0,0.5)' : 'none',
                    transition: 'all 0.2s ease',
                    transform: bannerGradient === gradient.value ? 'scale(1.1)' : 'scale(1)'
                  }}
                />
              ))}
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ alignSelf: 'flex-end', marginTop: '0.5rem' }} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </button>
        </form>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        
        <div className="card" style={{ border: '2px solid var(--border-color)', margin: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem' }}>
          <div>
            <h3 style={{ marginTop: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <LogOut size={22} color="var(--primary-color)" /> Changer de profil
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: '0.2rem 0 0 0', lineHeight: '1.4' }}>
              Passer à un autre profil d'athlète sur cet appareil.
            </p>
          </div>
          <button 
            className="btn btn-outline"
            onClick={() => {
              if (onLogout) onLogout();
              navigate('/');
            }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.6rem 1.2rem' }}
          >
            <LogOut size={18} /> Changer de profil
          </button>
        </div>

        <div className="card" style={{ border: '2px solid var(--border-color)', background: 'linear-gradient(to bottom right, var(--bg-card), rgba(108, 92, 231, 0.05))', margin: 0 }}>
          <h3 style={{ marginTop: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Download size={22} color="var(--primary-color)" /> Exportation des données
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
          Conformément au RGPD, vous êtes propriétaire de vos données. Vous pouvez à tout moment télécharger l'intégralité de votre historique, de vos programmes et de vos mensurations au format standard JSON.
        </p>
        <button className="btn btn-primary" onClick={handleExportJSON} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', width: '100%', justifyContent: 'center' }}>
          <Download size={20} /> Télécharger mes données (.json)
        </button>
      </div>

      <div className="card" style={{ border: '2px solid rgba(255, 71, 87, 0.2)', background: 'linear-gradient(to bottom right, var(--bg-card), rgba(255, 71, 87, 0.05))', margin: 0 }}>
        <h3 style={{ marginTop: 0, color: 'var(--danger-color)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Trash2 size={22} color="var(--danger-color)" /> Zone de danger
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
          Attention, la suppression d'un profil est irréversible. Toutes les données associées (séances, mensurations) seront perdues.
        </p>
        <button 
          className="btn" 
          onClick={handleDeleteProfile} 
          disabled={saving}
          style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', width: '100%', justifyContent: 'center', backgroundColor: 'var(--danger-color)', color: '#fff', border: 'none' }}
        >
          <Trash2 size={20} /> Supprimer mon profil
        </button>
        </div>
      </div>
      
      {isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem', marginTop: '2rem' }}>
          <div className="card" style={{ border: '2px solid var(--border-color)', margin: 0 }}>
            <h3 style={{ marginTop: 0, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <MessageSquare size={22} color="var(--primary-color)" /> Vos retours nous aident !
            </h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', marginBottom: '1.5rem', lineHeight: '1.5' }}>
              Un bug ? Une idée d'amélioration ? N'hésitez pas à nous en faire part !
            </p>
            <button className="btn btn-primary" onClick={() => setShowFeedbackModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', width: '100%', justifyContent: 'center' }}>
              <Bug size={20} /> Signaler un bug / Suggestion
            </button>
            <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Ou par e-mail : <a href="mailto:contact@masse-terclass.com" style={{ color: 'var(--primary-color)', textDecoration: 'none' }}>contact@masse-terclass.com</a>
            </p>
          </div>
        </div>
      )}



      </div>

      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div className="card" style={{ maxWidth: '400px', width: '90%', textAlign: 'center', animation: 'fadeIn 0.2s ease-out' }}>
            <h3 style={{ color: 'var(--danger-color)', marginTop: 0 }}>Confirmer la suppression</h3>
            <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)' }}>
              Êtes-vous sûr de vouloir supprimer définitivement le profil "{currentProfile.name}" ? Cette action est irréversible.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="btn" style={{ flex: 1, backgroundColor: 'var(--bg-color)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }} onClick={() => setShowDeleteConfirm(false)}>
                Annuler
              </button>
              <button className="btn" style={{ flex: 1, backgroundColor: 'var(--danger-color)', color: '#fff' }} onClick={executeDelete} disabled={saving}>
                {saving ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeedbackModal && <FeedbackModal currentProfile={currentProfile} onClose={() => setShowFeedbackModal(false)} />}
    </div>
  );
};

export default Settings;
